import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { AuthLayout } from "@/pages/Login";
import { api, errMsg } from "@/lib/api";

export default function RecuperoPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (fn) => {
    setErr("");
    setBusy(true);
    try { await fn(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  const request = (e) => {
    e.preventDefault();
    run(async () => { await api.post("/auth/forgot-password", { email }); setStep(2); });
  };
  const reset = (e) => {
    e.preventDefault();
    if (pw !== pw2) return setErr("Le password non coincidono");
    run(async () => {
      await api.post("/auth/reset-password", { email, code, new_password: pw });
      toast.success("Password aggiornata. Accedi con la nuova password.");
      nav("/login", { replace: true });
    });
  };

  return (
    <AuthLayout title="Recupero password" subtitle={step === 1 ? "Inserisci l'email del tuo account: ti invieremo un codice di verifica." : `Se ${email} è registrata, riceverai un codice di 6 cifre valido 15 minuti.`}>
      {step === 1 ? (
        <form onSubmit={request} className="space-y-5" data-testid="forgot-form">
          <div className="space-y-2">
            <Label htmlFor="fp-email">Email</Label>
            <Input id="fp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" data-testid="forgot-email-input" />
          </div>
          {err && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="forgot-error">{err}</p>}
          <Button type="submit" className="w-full" disabled={busy} data-testid="forgot-submit-button">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Invia codice
          </Button>
        </form>
      ) : (
        <form onSubmit={reset} className="space-y-5" data-testid="reset-form">
          <div className="flex items-center gap-2 rounded-md bg-primary/10 px-3 py-2 text-sm text-primary"><MailCheck className="h-4 w-4" />Controlla la tua casella email (anche lo spam).</div>
          <div className="space-y-2">
            <Label>Codice di verifica</Label>
            <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="reset-code-input">
              <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}</InputOTPGroup>
            </InputOTP>
          </div>
          <div className="space-y-2"><Label>Nuova password (min. 8)</Label><Input type="password" minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="reset-password-input" /></div>
          <div className="space-y-2"><Label>Conferma password</Label><Input type="password" minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} required data-testid="reset-password-confirm-input" /></div>
          {err && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="reset-error">{err}</p>}
          <Button type="submit" className="w-full" disabled={busy || code.length !== 6} data-testid="reset-submit-button">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Imposta nuova password
          </Button>
          <button type="button" onClick={() => run(() => api.post("/auth/forgot-password", { email }).then(() => toast.success("Nuovo codice inviato")))} className="text-xs text-muted-foreground hover:text-foreground" data-testid="reset-resend-button">
            Non hai ricevuto il codice? Invialo di nuovo
          </button>
        </form>
      )}
      <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-login-link"><ArrowLeft className="h-4 w-4" />Torna al login</Link>
    </AuthLayout>
  );
}
